import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Spin, Tooltip, Space, Tabs } from 'antd'
import Button from '../../../../core/components/Button.jsx'
import { RecordFormDeleteEllipses } from '../../../../core/components/RecordFormDeleteEllipses.tsx'
import { AgentProvenanceTag } from '../../../../core/components/AgentProvenanceTag.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { ContactRecordFields, ContactRecord } from '../../../../state_manager/data_models/contact_record.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { selectContactById } from '../../../../state_manager/state_update_actions/contacts/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  removeActiveRecord
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  updateContactRecord,
  removeContactFromList
} from '../../../../state_manager/state_update_actions/contacts/index.ts'
import { fetchContactById, deleteContact } from '../../services/contactsService.ts'
import { Drawer as DrawerNew } from './DrawerNew.tsx'
import { Drawer as DrawerExisting } from './DrawerExisting.tsx'
import { ChangeLogTab } from '../../../change_log/components/ChangeLogTab.tsx'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { removeTab, selectActiveTabKey, updateTabPath } from '../../../../store/slices/tabSlice'
import { stripTrailingFormRouteSegments } from '../../../../core/contexts/DrawerContext.jsx'
import { CONFIRM_DELETE_CONTACT_TITLE } from '../../../../state_manager/constants/strings.ts'

interface FullContactFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullContactForm({ recordId: propRecordId, isNew: propIsNew }: FullContactFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new'

  const contactsState = useSelector((state: any) => state.contacts)

  const contactFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )
  const contactFromState = useSelector((state: any) => (recordId && !isNew ? selectContactById(state, recordId) : null))
  const currentContact = contactFromActiveRecords || contactFromState

  const currentRolesKey = useMemo(() => {
    const roles = currentContact?.prog__current_roles
    if (!roles || !Array.isArray(roles)) {
      return roles === undefined ? undefined : `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    if (roles.length === 0) {
      return `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    return JSON.stringify(
      roles.map((r: any) => ({
        id: r[CompanyTeamEntryFields.contact_accounts__id] || r.contact_accounts__id,
        companyId: r[CompanyTeamEntryFields.contact_accounts__account_id] || r.contact_accounts__account_id,
        categoryId:
          r[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
          r.contact_accounts__relationship_category_id,
        current: r[CompanyTeamEntryFields.contact_accounts__current] ?? r.contact_accounts__current,
        fromDate: r.contact_accounts__from_date,
        toDate: r.contact_accounts__to_date
      }))
    )
  }, [currentContact?.prog__current_roles, currentContact?.[ContactRecordFields.contacts__id]])

  const priorRolesKey = useMemo(() => {
    const roles = currentContact?.prog__prior_roles
    if (!roles || !Array.isArray(roles)) {
      return roles === undefined ? undefined : `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    if (roles.length === 0) {
      return `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    return JSON.stringify(
      roles.map((r: any) => ({
        id: r[CompanyTeamEntryFields.contact_accounts__id] || r.contact_accounts__id,
        companyId: r[CompanyTeamEntryFields.contact_accounts__account_id] || r.contact_accounts__account_id,
        categoryId:
          r[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
          r.contact_accounts__relationship_category_id,
        current: r[CompanyTeamEntryFields.contact_accounts__current] ?? r.contact_accounts__current,
        fromDate: r.contact_accounts__from_date,
        toDate: r.contact_accounts__to_date
      }))
    )
  }, [currentContact?.prog__prior_roles, currentContact?.[ContactRecordFields.contacts__id]])

  const introductionsKey = useMemo(() => {
    const introductions = currentContact?.prog__introductions
    if (!introductions || !Array.isArray(introductions)) {
      return introductions === undefined
        ? undefined
        : `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    if (introductions.length === 0) {
      return `[]-${currentContact?.[ContactRecordFields.contacts__id] || 'no-id'}`
    }
    return JSON.stringify(
      introductions.map((i: any) => ({
        id: i[CompanyTeamEntryFields.contact_accounts__id] || i.contact_accounts__id,
        companyId: i[CompanyTeamEntryFields.contact_accounts__account_id] || i.contact_accounts__account_id,
        categoryId:
          i[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
          i.contact_accounts__relationship_category_id,
        current: i[CompanyTeamEntryFields.contact_accounts__current] ?? i.contact_accounts__current,
        fromDate: i.contact_accounts__from_date,
        toDate: i.contact_accounts__to_date
      }))
    )
  }, [currentContact?.prog__introductions, currentContact?.[ContactRecordFields.contacts__id]])

  const [formRef, setFormRef] = useState<{ submit: () => Promise<void> } | null>(null)
  const [isFetchingContact, setIsFetchingContact] = useState(false)
  const [isDeletingContact, setIsDeletingContact] = useState(false)
  const activeTabKey = useSelector((state: any) => selectActiveTabKey(state))
  const hasAttemptedFetch = useRef(false)
  const contactsStateRef = useRef(contactsState)

  useEffect(() => {
    contactsStateRef.current = contactsState
  }, [contactsState])

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId])

  const previousRecordIdRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const recordIdString = recordId?.toString()
    const previousRecordIdString = previousRecordIdRef.current?.toString()

    if (!isNew && recordIdString && recordIdString !== previousRecordIdString) {
      const currentContactNow = contactFromActiveRecords || contactFromState

      if (currentContactNow) {
        hasAttemptedFetch.current = true
        dispatch(setActiveRecord({ uuid: recordIdString, record: currentContactNow }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
      } else if (!hasAttemptedFetch.current) {
        const currentToken = getAccessToken(store.getState().auth)
        if (currentToken) {
          hasAttemptedFetch.current = true
          setIsFetchingContact(true)
          fetchContactById(recordIdString, currentToken)
            .then((response) => {
              if (response.data && !response.error) {
                const fetchedContact = response.data as ContactRecord
                dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedContact }))
                dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
              } else if (response.error) {
                console.error('Error fetching contact:', response.error)
              }
            })
            .catch((error) => {
              console.error('Error fetching contact:', error)
            })
            .finally(() => {
              setIsFetchingContact(false)
            })
        }
      }
      previousRecordIdRef.current = recordId
    }
  }, [recordId, isNew, dispatch, contactFromActiveRecords, contactFromState])

  useEffect(() => {
    return () => {
      if (recordId && !isNew) {
        const contactIdString = recordId.toString()
        dispatch(unregisterRecordUsage({ uuid: contactIdString, context: 'fullForm' }))
      }
    }
  }, [recordId, isNew, dispatch])

  const handleDeleteContact = useCallback(async () => {
    if (!recordId || isNew) return
    const token = getAccessToken(store.getState().auth)
    if (!token) {
      showErrorToast('Not authenticated')
      return
    }
    setIsDeletingContact(true)
    try {
      await deleteContact(recordId, token)
      toast.success('Record deleted')
      const id = String(recordId)
      dispatch(removeContactFromList({ contactId: id }))
      dispatch(removeActiveRecord({ uuid: id }))
      dispatch(unregisterRecordUsage({ uuid: id, context: 'fullForm' }))
      window.isClosingTab = true
      dispatch(removeTab({ key: activeTabKey }))
      const nextState = store.getState().tabs
      const nextTab = nextState.tabs.find((t: { key: string }) => t.key === nextState.activeTabKey)
      if (nextTab?.path) {
        const pathWithoutFormShell = stripTrailingFormRouteSegments(nextTab.path)
        if (pathWithoutFormShell !== nextTab.path) {
          dispatch(updateTabPath({ key: nextTab.key, path: pathWithoutFormShell }))
        }
        navigate(pathWithoutFormShell, { replace: true })
      }
      setTimeout(() => {
        window.isClosingTab = false
      }, 150)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to delete contact')
    } finally {
      setIsDeletingContact(false)
    }
  }, [recordId, isNew, dispatch, navigate, activeTabKey])

  const handleRefresh = useCallback(async () => {
    if (!recordId || isNew) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) {
      return
    }

    setIsFetchingContact(true)
    try {
      const result = await fetchContactById(recordId, currentToken)
      if (result && result.data) {
        const recordIdString = recordId.toString()
        dispatch(setActiveRecord({ uuid: recordIdString, record: result.data }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
        dispatch(updateContactRecord({ contactId: recordIdString, updates: result.data }))
        toast.success('Refreshed Successfully')
      }
    } catch (error) {
      console.error('Error refreshing contact:', error)
    } finally {
      setIsFetchingContact(false)
    }
  }, [recordId, isNew, dispatch])

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        navigate('/contacts')
      } catch (error) {
        console.error('Error submitting form:', error)
      }
    },
    [navigate]
  )

  const shouldShowLoading = !isNew && !currentContact && isFetchingContact

  if (shouldShowLoading) {
    return (
      <div className='h-full'>
        <div className='flex justify-center items-center h-full'>
          <Spin size='large' />
        </div>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col overflow-hidden'>
      {!isNew && (
        <div className='flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0'>
          <div className='min-w-0'>
            <AgentProvenanceTag provenance={currentContact?.[ContactRecordFields.prog__created_provenance]} />
          </div>
          <Space size='small'>
            <RecordFormDeleteEllipses
              confirmTitle={CONFIRM_DELETE_CONTACT_TITLE}
              onDelete={handleDeleteContact}
              loading={isDeletingContact}
              disabled={isFetchingContact}
            />
            <Tooltip title='Refresh'>
              <Button
                variant='default'
                data-debug='refresh-full-form-btn'
                size='small'
                icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
                onClick={handleRefresh}
                loading={isFetchingContact}
                disabled={isDeletingContact}
                style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
              />
            </Tooltip>
          </Space>
        </div>
      )}
      {isNew ? (
        <div className='flex-1 overflow-y-auto overflow-x-hidden p-4'>
          <DrawerNew contact={null} onSubmit={handleSubmit} loading={isFetchingContact} ref={setFormRef} />
        </div>
      ) : (
        <div className='flex-1 min-h-0 px-4 pt-2'>
          <Tabs
            type='line'
            size='middle'
            className='deal-form-tabs h-full'
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <div data-debug='full-form-details-tab' className='h-full overflow-y-auto overflow-x-hidden py-4'>
                    <DrawerExisting
                      contact={currentContact}
                      onSubmit={handleSubmit}
                      loading={isFetchingContact}
                      currentRolesKey={currentRolesKey}
                      priorRolesKey={priorRolesKey}
                      introductionsKey={introductionsKey}
                    />
                  </div>
                )
              },
              ...(recordId
                ? [
                    {
                      key: 'change-log',
                      label: 'Change Log',
                      children: (
                        <div data-debug='full-form-change-log-tab' className='h-full'>
                          <ChangeLogTab key={`change-log-${recordId}`} contextTable='contacts' recordId={recordId} />
                        </div>
                      )
                    }
                  ]
                : [])
            ]}
          />
        </div>
      )}
    </div>
  )
}
